import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  X, 
  Copy, 
  Share2, 
  UserPlus, 
  Users,
  ExternalLink,
  MessageCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { couponsService } from '@/services/coupons.service';
import type { CampanhaCupom } from '@/lib/supabase';

interface LinksIndicacaoProps {
  hideRevendedoraCard?: boolean;
  onClose?: () => void;
}

const LinksIndicacao: React.FC<LinksIndicacaoProps> = ({ hideRevendedoraCard = false, onClose }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [userData, setUserData] = useState<any>(null);
  const [campanhaAtiva, setCampanhaAtiva] = useState<CampanhaCupom | null>(null);

  useEffect(() => {
    const userDataString = localStorage.getItem('userlogged');
    if (userDataString) {
      const parsedUserData = JSON.parse(userDataString);
      setUserData(parsedUserData);
    }

    // Carregar campanha ativa
    const loadCampanha = async () => {
      try {
        const campanha = await couponsService.getCampanhaAtiva();
        setCampanhaAtiva(campanha);
      } catch (error) {
        console.error('Erro ao carregar campanha:', error);
      }
    };

    loadCampanha();
  }, []);

  const handleClose = () => {
    if (onClose) {
      onClose(); // Se houver callback, usa ele
    } else {
      navigate(-1); // Senão, volta para a página anterior
    }
  };

  const generateClientLink = () => {
    if (!userData) return '';
    
    // Se houver campanha ativa, usa o link da LP com o código de referência
    if (campanhaAtiva) {
      return `https://xdoinfinitto.com.br/lp/cada-amiga-vale-diamantes/?ref=${userData.codigo}`;
    }
    
    // Senão, usa o link interno
    const baseUrl = window.location.origin;
    return `${baseUrl}/v2/cadastro-cliente/${userData.codigo}`;
  };

  const generateRevendedoraLink = () => {
    if (!userData) return '';
    return `https://xdoinfinitto.com.br/v2/revender/${userData.codigo}`;
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: 'Link copiado!',
        description: `Link de indicação ${type} copiado para a área de transferência.`,
        variant: 'default',
        duration: 2000
      });
    } catch (error) {
      console.error('Erro ao copiar link:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível copiar o link. Tente novamente.',
        variant: 'destructive',
        duration: 3000
      });
    }
  };

  const shareLink = async (text: string, title: string, url: string) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: text,
          url: url
        });
      } catch (error) {
        console.error('Erro ao compartilhar:', error);
        // Fallback para copiar
        copyToClipboard(url, title.toLowerCase());
      }
    } else {
      // Fallback para copiar
      copyToClipboard(url, title.toLowerCase());
    }
  };

  const handleShareClient = () => {
    const link = generateClientLink();
    const text = `Quero te convidar para participar da Xizinha, o app de recompensas da X do Infinito.

É só se cadastrar pelo meu link e, a cada compra de semijoias X do Infinito, você já começa a acumular diamantes, para trocar por recompensas incríveis.
Segue o link: ${link}`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleCopyClient = () => {
    const link = generateClientLink();
    copyToClipboard(link, 'de cliente');
  };

  const handleShareRevendedora = () => {
    const link = generateRevendedoraLink();
    const text = `Tenho uma oportunidade incrível pra você: revender X do Infinito!

Basta entrar pelo meu link, preencher o pré cadastro e você já pode começar a lucrar com as semijoias!
Segue o link: ${link}`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleCopyRevendedora = () => {
    const link = generateRevendedoraLink();
    copyToClipboard(link, 'de revendedora');
  };

  if (!userData) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end lg:items-center lg:justify-center">
      {/* Bottom Sheet Container */}
      <div className="bg-background w-full lg:w-full lg:max-w-4xl lg:mx-auto lg:max-h-[90vh] rounded-t-3xl lg:rounded-3xl shadow-xl animate-in slide-in-from-bottom duration-300 lg:animate-in lg:fade-in lg:zoom-in-95 lg:flex lg:flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b lg:flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-foreground">Links de Indicação</h2>
            <p className="text-sm text-muted-foreground">Compartilhe e ganhe com suas indicações</p>
          </div>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleClose}
            className="hover:bg-muted"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto lg:overflow-y-auto lg:flex-1 lg:max-h-none">
          {/* Card - Indique Clientes */}
          <Card className="border-2 border-primary/20 hover:border-primary/40 transition-colors">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-primary" />
                </div>
                <span className="text-lg font-semibold">Indique Clientes</span>
                  {/* <div> <Badge variant="secondary" className="ml-2 text-xs">
                    Interno
                  </Badge>
                </div> */}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-3">
                  Convide pessoas para se tornarem clientes e acumule diamantes através de toda a rede.<sup>*</sup> 
                  Elas também ganham benefícios especiais!
                </p>
                
                {/* <div className="bg-muted/50 rounded-lg p-3 mb-3">
                  <p className="text-xs font-medium text-foreground mb-1">Seu link:</p>
                  <p className="text-xs text-muted-foreground break-all font-mono">
                    {generateClientLink()}
                  </p>
                </div> */}
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={handleCopyClient}
                  variant="outline" 
                  className="flex-1 border-gray-500"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar Link
                </Button>
                <Button 
                  onClick={handleShareClient}
                  className="flex-1"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Compartilhar
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Card - Indique Revendedoras */}
          {!hideRevendedoraCard && (
            <Card className="border-2 border-purple-200 hover:border-purple-300 transition-colors">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <span className="text-lg font-semibold">Indique Revendedoras</span>
                     {/* <Badge variant="outline" className="ml-2 text-xs border-purple-200 text-purple-700">
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Externo
                    </Badge>*/}
                  </div> 
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Indique pessoas para se tornarem revendedoras e mostre a oportunidade de empreender com a X do Infinito.<br />
                    Ganhe diamantes de todas as vendas das suas indicadas!
                  </p>
                  
                  {/* <div className="bg-muted/50 rounded-lg p-3 mb-3">
                    <p className="text-xs font-medium text-foreground mb-1">Seu link:</p>
                    <p className="text-xs text-muted-foreground break-all font-mono">
                      {generateRevendedoraLink()}
                    </p>
                  </div> */}
                </div>

                <div className="flex gap-2">
                  <Button 
                    onClick={handleCopyRevendedora}
                    variant="outline" 
                    className="flex-1 border-gray-500"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar Link
                  </Button>
                  <Button 
                    onClick={handleShareRevendedora}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Compartilhar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Card - Código de Indicação */}
          <Card className="border-2 border-green-200 hover:border-green-300 transition-colors">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Copy className="w-5 h-5 text-green-600" />
                </div>
                <span className="text-lg font-semibold">Seu Código de Indicação</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-3">
                  Você também pode compartilhar seu código para revendedoras e clientes inserirem no momento do cadastro!
                </p>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">                  
                  <p className="text-2xl font-bold text-green-600 text-center">
                    {userData?.codigo || '----'}
                  </p>
                </div>
              </div>

              <Button 
                onClick={() => copyToClipboard(userData?.codigo?.toString() || '', 'código')}
                variant="outline" 
                className="w-full border-green-500 text-green-600 hover:bg-green-50"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copiar Código
              </Button>
            </CardContent>
          </Card>

          {/* Informações adicionais */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">            
              <div className="space-y-2 text-sm">
                <h4 className="font-medium text-blue-900">💡 Dicas e observações:</h4>
                <ul className="text-blue-700 space-y-1 text-xs">
                  <li>• Você pode compartilhar o link de indicação ou enviar o seu código de indicação.</li>
                  <li>• Sempre oriente sua indicada a informar corretamente seu código no momento do cadastro, caso não entre pelo link.</li>
                  <li>• Explique que, ao se cadastrar, ela começa a acumular diamantes em todas as compras/vendas e ativações.</li>
                  <li>• Mostre as vantagens de ser cliente ou revendedora X do Infinito, destacando qualidade, suporte e recompensas reais.</li>
                  <li>• Acompanhe suas indicações pelo aplicativo e oriente para que elas se ativem e não deixem de receber seus bônus.</li>
                </ul>
                <hr className="border-blue-200" />
                <ul className="text-blue-400 space-y-1 text-xs">
                    <li>• Você recebe diamantes em até 3 níveis de indicação, aumentando suas chances de resgatar mais recompensas.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - apenas no mobile */}
        <div className="p-6 border-t lg:hidden lg:flex-shrink-0">
          <Button 
            onClick={handleClose}
            variant="outline" 
            className="w-full"
          >
            Fechar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LinksIndicacao;
